import Link from 'next/link';
import styled from 'styled-components';

import Footer from '@components/Footer';
import Navigation from '@components/Navigation';
import { brand, fontDark, fontMedium } from '@constants/colors';
import { address, contact, details } from '@constants/company-info';
import { login, privacy, productionUrl } from '@constants/routes';
import { addBreadcrumbsJsonLd } from '@constants/seo';
import { contentWidth } from '@constants/sizes';
import { space } from '@constants/spaces';
import CustomHead from '@elements/CustomHead';
import Title from '@elements/Title';

const Container = styled.main`
  padding: ${space * 10}px ${space * 4}px ${space * 9}px;
  max-width: ${contentWidth};
  margin: 0 auto;

  h2 {
    color: ${fontDark};
  }

  h2,
  em,
  p,
  ul {
    margin-bottom: ${space * 2}px;
  }

  p,
  ul,
  ol {
    color: ${fontMedium};
  }

  ul,
  ol {
    list-style-position: inside;
  }

  a {
    color: ${brand};
  }

  em {
    font-weight: 500;
  }
`;

const SyledTitle = styled(Title)`
  text-align: center;
`;

const breadcrumbs = [
  {
    '@type': 'ListItem',
    position: 1,
    name: 'Privacy- en cookiebeleid',
  },
];

const Privacy = () => {
  return (
    <>
      <CustomHead
        title="Privacy- en cookiebeleid"
        canonicalUrl={`${productionUrl}${privacy}`}
      >
        <script
          type="application/ld+json"
          key="privacy-breadcrumbs-jsonld"
          dangerouslySetInnerHTML={addBreadcrumbsJsonLd(breadcrumbs)}
        />
      </CustomHead>
      <Navigation />
      <Container>
        <SyledTitle size="lg" className="mb-40">
          Privacy Statement
        </SyledTitle>
        <p>
          Belmondo respecteert jouw privacy. Wij stellen alles in het werk om
          jouw privacy te beschermen. Met dit privacy statement willen wij jou
          duidelijk maken waarom wij bepaalde informatie verzamelen, hoe wij
          jouw privacy beschermen en onder welke voorwaarden wij de informatie
          aan je verstrekken.
        </p>
        <h2>Wie is Belmondo?</h2>
        <p>
          Belmondo BV, gevestigd aan {address.address}, {address.zipcode}{' '}
          {address.city}, is een bedrijf dat mensen helpt heel eenvoudig alleen
          of samen met anderen je foto&apos;s en verhalen in een fotoboek vast
          te leggen. In meer dan 30 thema&apos;s, van collega&apos;s tot
          vakantie en van vrienden tot koken, er is voor iedereen wel een
          geschikt onderwerp om op een eenvoudige manier, op je computer, tablet
          of telefoon, een bijzonder verhaal vast te leggen en te laten
          bestellen als fotoboek.
        </p>
        <p>
          Belmondo is verwerkingsverantwoordelijke voor de verwerking van
          persoonsgegevens zoals weergegeven in deze Privacy Statement.
        </p>
        <h2>Wanneer is dit privacybeleid van toepassing?</h2>
        <p>
          Dit privacybeleid is van toepassing op alle persoonsgegevens die
          Belmondo verwerkt voor haar dienstverlening door middel van haar
          website {details.website}.
        </p>
        <h2>Jouw gegevens in vertrouwde handen</h2>
        <p>
          Wil je gebruik maken van onze dienstverlening? Dan hebben wij jouw
          gegevens nodig. We verwerken je gegevens ten behoeve van de (te
          sluiten) overeenkomst, omdat je daar (uitdrukkelijk) toestemming voor
          gegeven hebt, er een wettelijke grondslag voor is of omdat wij een
          gerechtvaardigd belang hebben. Zonder jouw persoonsgegevens, kunnen
          wij geen overeenkomst met je afsluiten. Wij vinden het belangrijk dat
          je weet wat wij met jouw persoonsgegevens doen en wat jouw rechten
          zijn. Zodat je erop kunt vertrouwen dat jouw gegevens bij ons in goede
          handen zijn.&nbsp;
        </p>
        <h2>Persoonsgegevens die wij verwerken</h2>
        <p>
          Belmondo verwerkt je persoonsgegevens doordat je gebruik maakt van
          onze diensten en/of omdat je deze gegevens zelf aan ons verstrekt.
          <br />
          Hieronder vind je een overzicht van de persoonsgegevens die wij
          verwerken:
        </p>
        <p>
          <em>Voor het aanmaken van een account</em>
        </p>
        <p>
          Om te beginnen met het maken van je fotoboek op {details.website} heb
          je alleen een account nodig. Het account kan je aanmaken met de
          volgende gegevens:
        </p>
        <p>
          -&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Voor- en achternaam
          <br />
          -&nbsp; &nbsp; &nbsp; &nbsp;E-mailadres
        </p>
        <p>
          Dit e-mailadres wordt gebruikt om je informatie te sturen omtrent je
          bestelling. Ook kunnen we, na jouw uitdrukkelijke toestemming, dit
          e-mailadres gebruiken om je op de hoogte te houden van nieuws en
          aanbiedingen omtrent Belmondo.
        </p>
        <p>
          Indien je een bestelling hebt gedaan, kunnen wij je op dit e-mailadres
          productaanbevelingen sturen voor soortgelijke goederen. We houden ons
          hierbij strikt aan de wettelijke vereisten. Uiteraard kunt je op elk
          moment en zonder opgave van redenen kosteloos bezwaar aantekenen tegen
          onze productaanbevelingen. Richt je bezwaar tegen&nbsp;
          <a href={`mailto:${contact.email}`}>{contact.email}</a> of gebruik de
          afmeldingslink in onze e-mails.
        </p>
        <p>
          <em>Voor het bestellen van een fotoboek:</em>
        </p>
        <p>
          Als je fotoboek af is en je wilt het fotoboek bestellen, vragen wij
          verder naar:
        </p>
        <p>
          -&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Adres
          <br />
          -&nbsp; &nbsp; &nbsp; &nbsp;Telefoonnummer (niet verplicht)
          <br />
          -&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Gender (niet verplicht)
        </p>
        <p>
          Je adres zullen we uitsluitend gebruiken voor het verzenden van jouw
          bestelling. Je telefoonnummer kan je optioneel opgeven, en kunnen wij
          gebruiken om contact met je op te nemen over je bestelling. Je gender
          kan je ook vrijwillig opgegeven en gebruiken wij om je correct aan te
          spreken.
        </p>
        <p>
          <em>Voor het deelnemen aan een fotoboek:</em>
        </p>
        <p>
          -&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Voor- en/of achternaam
          <br />
          -&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; E-mailadres
        </p>
        <p>
          Ben je uitgenodigd om samen te werken aan een fotoboek als deelnemer?
          Dan heeft iemand anders jouw e-mailadres aan ons doorgegeven. Jouw
          e-mailadres wordt alleen gebruikt om je toegang te geven tot het
          gezamenlijke fotoboek en daaraan samen te werken.
        </p>
        <h2>Bijzondere en/of gevoelige persoonsgegevens die wij verwerken</h2>
        <p>
          Onze website en/of dienst heeft niet de intentie gegevens te
          verzamelen over websitebezoekers die jonger zijn dan 16 jaar. Tenzij
          ze toestemming hebben van ouders of voogd. We kunnen echter niet
          controleren of een bezoeker ouder dan 16 is. Wij raden ouders dan ook
          aan betrokken te zijn bij de online activiteiten van hun kinderen, om
          zo te voorkomen dat er gegevens over kinderen verzameld worden zonder
          ouderlijke toestemming. Als je ervan overtuigd bent dat wij zonder die
          toestemming persoonlijke gegevens hebben verzameld over een
          minderjarige, neem dan contact met ons op via{' '}
          <a href={`mailto:${contact.email}`}>{contact.email}</a>, dan
          verwijderen wij deze informatie.
        </p>
        <p>
          Belmondo verwerkt de foto&apos;s en/of afbeeldingen die gebruikers
          zelf uploaden in ons fotoboek-bewerkingsprogramma. De foto&apos;s
          worden uitsluitend gebruikt voor het fotoboek waaraan de gebruikers
          werken, als hoofgebruiker of deelnemer. Deze foto&apos;s staan goed
          beschermd op een externe server. Twee jaar na het afleveren van het
          fotoboek worden deze foto&apos;s automatisch van onze server
          verwijderd.
        </p>
        <h2>Hoe lang we persoonsgegevens bewaren</h2>
        <p>
          Belmondo bewaart je persoonsgegevens niet langer dan strikt nodig is
          om de doelen te realiseren waarvoor je gegevens worden verzameld. Wij
          houden ons daarbij aan de wettelijke bewaartermijnen.
        </p>
        <h2>Delen van persoonsgegevens met derden</h2>
        <p>
          Belmondo deelt jouw persoonsgegevens met verschillende derden als dit
          noodzakelijk is voor het uitvoeren van de overeenkomst en om te
          voldoen aan een eventuele wettelijke verplichting. Met bedrijven die
          je gegevens verwerken in onze opdracht, sluiten wij een
          bewerkersovereenkomst om te zorgen voor eenzelfde niveau van
          beveiliging en vertrouwelijkheid van jouw gegevens. Belmondo blijft
          verantwoordelijk voor deze verwerkingen. Daarnaast verstrekt Belmondo
          jouw persoonsgegevens aan andere derden. Dit doen wij alleen met jouw
          uitdrukkelijke toestemming.
        </p>
        <p>
          <em>Afdrukken en logistiek</em>
        </p>
        <p>
          Belmondo deelt jouw adresgegevens en het jouw fotoboek met een
          drukkerij, die verder de verwerking van jouw bestelling op zich neemt.
          Deze partij ontvangt jouw fotoboek en drukt deze af. Daarnaast
          ontvangen zij ook jouw klantnummer, bestelnummer, naam en bezorgadres
          om te zorgen dat ze jouw pakketje op de juiste manier kunnen verwerken
          en versturen.
        </p>
        <p>
          <em>Andere externe dienstverleners</em>
        </p>
        <p>
          Wij schakelen ook andere externe dienstverleners in om te helpen met
          verschillende dingen. Soms hebben zij daar ook persoonsgegevens voor
          nodig. Wij delen alleen de gegevens die noodzakelijk zijn voor de
          opdracht die de externe dienstverlener voor ons uitvoert. We verkopen
          je gegevens niet aan deze dienstverleners of andere derde partijen. En
          met alle externe dienstverleners sluiten wij overeenkomsten waarin wij
          afspreken wat ze met de gegevens mogen doen.
        </p>
        <p>
          Het type werkzaamheden dat onze externe dienstverleners voor ons
          uitvoeren zijn:
        </p>
        <p>
          -&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; het ondersteunen bij het leveren
          van onze digitale diensten zoals de hosting, onderhoud en
          ondersteuning van onze websites en applicaties;
          <br />
          -&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; het ondersteunen bij onze
          advertentie- en marketingcampagnes;
          <br />
          -&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; het leveren van financiële
          diensten, zoals betaaldiensten, incassobureaus en
          kredietinformatiebureaus.
        </p>
        <h2>Hoe wij persoonsgegevens beveiligen</h2>
        <p>
          Belmondo neemt de bescherming van jouw gegevens serieus en neemt
          passende maatregelen om misbruik, verlies, onbevoegde toegang,
          ongewenste openbaarmaking en ongeoorloofde wijziging tegen te gaan.
          Als jij het idee hebt dat jouw gegevens toch niet goed beveiligd zijn
          of er aanwijzingen zijn van misbruik, neem dan contact op met onze
          klantenservice of via{' '}
          <a href={`mailto:${contact.email}`}>{contact.email}</a>.
        </p>
        <h2>Cookies, of vergelijkbare technieken, die wij gebruiken</h2>
        <p>
          Belmondo gebruikt functionele, analytische en tracking cookies. Een
          cookie is een klein tekstbestand dat bij het eerste bezoek aan deze
          website wordt opgeslagen in de browser van je computer, tablet of
          smartphone. Belmondo gebruikt cookies met een puur technische
          functionaliteit. Deze zorgen ervoor dat de website naar behoren werkt
          en dat bijvoorbeeld jouw voorkeursinstellingen onthouden worden. Deze
          cookies worden ook gebruikt om de website goed te laten werken en deze
          te kunnen optimaliseren. Daarnaast plaatsen we cookies die jouw
          surfgedrag bijhouden zodat we op maat gemaakte content en advertenties
          kunnen aanbieden.
        </p>
        <p>
          Deze website maakt gebruik van Google Analytics voor analyses van
          geanonimiseerd bezoek- en klikgedrag. Met Google is een
          verwerkersovereenkomst afgesloten. Belmondo heeft Google geen
          toestemming gegeven om deze informatie te delen met andere
          Google-diensten.
        </p>
        <p>
          Bij jouw eerste bezoek aan onze website hebben wij je al geïnformeerd
          over deze cookies en hebben we je toestemming gevraagd voor het
          plaatsen ervan.
        </p>
        <p>
          Je kunt je afmelden voor cookies door je internetbrowser zo in te
          stellen dat deze geen cookies meer opslaat. Daarnaast kun je ook alle
          informatie die eerder is opgeslagen via de instellingen van je browser
          verwijderen.
        </p>
        <p>
          Zie voor een toelichting:{' '}
          <a href="https://veiliginternetten.nl/themes/situatie/cookies-wat-zijn-het-en-wat-doe-ik-ermee/">
            https://veiliginternetten.nl/themes/situatie/cookies-wat-zijn-het-en-wat-doe-ik-ermee/
          </a>
        </p>
        <h2>Gegevens inzien, aanpassen of verwijderen</h2>
        <p>
          Je hebt de volgende rechten met betrekking tot de verwerking van je
          persoonsgegevens:
        </p>
        <ul>
          <li>je gegevens op te vragen;</li>
          <li>je gegevens aan te passen;</li>
          <li>je gegevens te verwijderen;</li>
          <li>bezwaar tegen de verwerking te maken;</li>
          <li>je gegevens over te dragen;</li>
          <li>je toestemming in te trekken;</li>
          <li>de verwerking van je gegevens tijdelijk te stoppen.</li>
        </ul>
        <p>
          Heb je een account aangemaakt en/of een bestelling geplaatst? Dan kan
          je <Link href={login}>inloggen</Link> om te zien welke informatie we
          van je hebben opgeslagen. Deze informatie is hier ook te bewerken. Ook
          kan je je in het account eerdere bestellingen bekijken en in- of
          uitschrijven voor onze nieuwsbrief.
        </p>
        <p>
          Daarnaast heb je het recht om je eventuele toestemming voor de
          gegevensverwerking in te trekken of bezwaar te maken tegen de
          verwerking van jouw persoonsgegevens door Belmondo en heb je het recht
          op gegevensoverdraagbaarheid. Dat betekent dat je bij ons een verzoek
          kan indienen om de persoonsgegevens die wij van jou beschikken in een
          computerbestand naar jou of een ander, door jou genoemde organisatie,
          te sturen.
        </p>
        <p>
          Je kunt een verzoek tot inzage, correctie, verwijdering,
          gegevensoverdraging van je persoonsgegevens of verzoek tot intrekking
          van je toestemming of bezwaar op de verwerking van jouw
          persoonsgegevens sturen naar{' '}
          <a href={`mailto:${contact.email}`}>{contact.email}</a>.
        </p>
        <h2>Laat ons weten als je gebruik wilt maken van jouw rechten</h2>
        <p>
          Stuur ons een brief of e-mail en stuur een kopie van je paspoort of
          ID-kaart mee. Wil je je Burgerservicenummer (BSN) en pasfoto
          alstjeblieft onleesbaar maken? Wij reageren binnen een maand na
          ontvangst van je brief of e-mail.
        </p>
        <h2>Heb je een vraag, tip of klacht?</h2>
        <p>
          Stuur een e-mail naar ons via{' '}
          <a href={`mailto:${contact.email}`}>{contact.email}</a>. Je kunt ook
          een brief sturen naar:
        </p>
        <p>
          {address.name}
          <br />
          {address.address}
          <br />
          {address.zipcode} {address.city}
        </p>
        <h2>Komen wij er samen niet uit?</h2>
        <p>
          Dan kun je je klacht ook altijd voorleggen aan de{' '}
          <a href="https://autoriteitpersoonsgegevens.nl/nl/contact-met-de-autoriteit-persoonsgegevens/tip-ons">
            Autoriteit Persoonsgegevens
          </a>
          .
        </p>
        <h2>Wij kunnen dit privacy statement veranderen</h2>
        <p>
          Dat mag bijvoorbeeld als er iets verandert in de wetten of regels of
          als wij nieuwe producten of diensten ontwikkelen.
        </p>
      </Container>
      <Footer />
    </>
  );
};

export default Privacy;
